import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Arrow from '@/assets/svg/arrow-down.svg';
import CheckIcon from '@/assets/svg/checkbox-tick.svg';
import { colors } from '@/constants/theme';
import { ScrollStyles, TextLimit } from '@/components/styles';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import CrossIcon from '@/assets/svg/close-12.svg';
import TippyLockIcon from '@/components/StockFilterMultiSelectParts/components/TippyLockIcon';
import Link from '@/components/Link';

import {
  StockFilterMultiSelectPartsOptionType,
  StockFilterMultiSelectPartsSelectedOptionType,
  StockFilterMultiSelectPartsProps,
} from './types';

function StockFilterMultiSelectParts({
  label,
  options,
  selectedOptions,
  disabled,
  disabledHint,
  onChange,
  onChangeAll,
  onClear,
}: StockFilterMultiSelectPartsProps) {
  const [isOpen, setOpened] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(componentRef, () => {
    if (isOpen) {
      setOpened(false);
    }
  });

  const handleOpenSelect = () => {
    setOpened(!isOpen);
  };

  const handleOptionClick = (
    option: StockFilterMultiSelectPartsSelectedOptionType
  ) => {
    onChange?.(option);
  };

  const handleOptionsAllClick = (
    options: StockFilterMultiSelectPartsOptionType
  ) => {
    onChangeAll?.(options);
  };

  const handleClear = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    onClear?.();
  };

  return (
    <Component>
      {label && <Label>{label}</Label>}

      <SelectWrapper ref={componentRef}>
        <Select onClick={handleOpenSelect} disabled={disabled}>
          <Picked>
            {selectedOptions &&
              selectedOptions.length > 0 &&
              selectedOptions.map((item) => item.label).join(', ')}
          </Picked>

          <Widgets>
            {selectedOptions && selectedOptions.length > 0 && (
              <PickedLabel>Выбрано {selectedOptions.length}</PickedLabel>
            )}
            {onClear && selectedOptions && selectedOptions.length > 0 && (
              <ClearButton onClick={handleClear}>
                <CrossIcon />
              </ClearButton>
            )}
            {disabled ? (
              <TippyLockIcon disabledHint={disabledHint} />
            ) : (
              <IconWrapper isOpen={isOpen}>
                <Arrow />
              </IconWrapper>
            )}
          </Widgets>
        </Select>

        <Content isOpen={isOpen}>
          {options && options.length !== 0 ? (
            options.map((parts, index) => {
              const isActive =
                parts.options.length > 0
                  ? parts.options.length ===
                    (selectedOptions &&
                      selectedOptions.filter(
                        (selectedOption) => selectedOption.title === parts.title
                      ).length)
                  : parts.title ===
                    (selectedOptions &&
                      selectedOptions.find(
                        (option) => option.title === parts.title
                      )?.title);

              return (
                <Parts key={index}>
                  <PartsTitle
                    isActive={isActive}
                    onClick={() => handleOptionsAllClick(parts)}
                  >
                    {parts.title}
                    {isActive && <CheckIcon />}
                  </PartsTitle>
                  <Options>
                    {parts.options.map((option, index) => {
                      const isActive = selectedOptions?.find(
                        (selectedOption) =>
                          selectedOption.title === parts.title &&
                          selectedOption.value === option.value
                      );

                      const optionInner = (
                        <React.Fragment>
                          {option.label}
                          {isActive && <CheckIcon />}
                        </React.Fragment>
                      );

                      return !option.link ? (
                        <Option
                          key={index}
                          isActive={!!isActive}
                          onClick={() =>
                            handleOptionClick({
                              title: parts.title,
                              label: option.label,
                              value: option.value,
                            })
                          }
                        >
                          {optionInner}
                        </Option>
                      ) : (
                        <OptionLink
                          key={index}
                          to={option.link}
                          isActive={!!isActive}
                          onClick={(
                            event: React.MouseEvent<
                              HTMLAnchorElement,
                              MouseEvent
                            >
                          ) => {
                            event.preventDefault();
                            handleOptionClick({
                              title: parts.title,
                              label: option.label,
                              value: option.value,
                            });
                          }}
                        >
                          {optionInner}
                        </OptionLink>
                      );
                    })}
                  </Options>
                </Parts>
              );
            })
          ) : (
            <NoOptions>Нет опций</NoOptions>
          )}
        </Content>
      </SelectWrapper>
    </Component>
  );
}

export default StockFilterMultiSelectParts;

const Component = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.p`
  font-size: 16px;
  line-height: 19px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.blue100};
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.gray1000};
  border-radius: 6px;
`;

const Select = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 15px 10px;
  height: 38px;

  &:disabled {
    cursor: default;
  }
`;

const Picked = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 19px;
  text-align: left;
  color: ${(props) => props.theme.black200};

  ${TextLimit};
`;

const Widgets = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  transition: 0.3s;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 9px;
    height: 9px;

    path {
      fill: ${(props) => props.theme.main};
    }
  }
`;

const PickedLabel = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  margin-right: 10px;
  color: ${(props) => props.theme.gray700};
`;

const IconWrapper = styled.div<{ isOpen: boolean }>`
  svg {
    transition: 0.3s;
    transform: rotate(0deg);

    path {
      fill: ${(props) => props.theme.black200};
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      svg {
        transform: rotate(180deg);

        path {
          fill: ${(props) => props.theme.main};
        }
      }
    `}
`;

const Content = styled.div<{ isOpen: boolean }>`
  z-index: 5;
  pointer-events: none;
  position: absolute;
  top: 40px;
  width: 100%;
  max-height: 0;
  overflow-y: auto;
  transition: all 0.3s ease-in-out;
  background: ${(props) => props.theme.white_1};
  opacity: 0;
  border: 1px solid ${(props) => props.theme.gray1000};
  box-shadow: ${(props) => props.theme.blackShadow100};
  ${ScrollStyles};

  ${({ isOpen }) =>
    isOpen &&
    css`
      pointer-events: auto;
      opacity: 1;
      max-height: 280px;
      transition: all 0.3s ease-in-out;
    `}
`;

const Parts = styled.div``;

const PartsTitle = styled.div<{ isActive?: boolean }>`
  cursor: pointer;
  padding: 10px 30px 10px 15px;
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  height: 39px;
  color: ${(props) => props.theme.black};

  ${({ isActive }) =>
    isActive &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;

      svg {
        flex: 0 0 23px;
        height: 19px;

        path {
          fill: ${colors.main};
          stroke: ${colors.main};
        }
      }
    `}
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionStyles = css<{ isActive?: boolean }>`
  cursor: pointer;
  height: 39px;
  padding: 10px 30px;
  font-size: 16px;
  line-height: 19px;
  transition: all 0.3s ease-in-out;
  color: ${(props) => props.theme.blue100};

  &:hover {
    background: ${(props) => props.theme.gray300};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;

      svg {
        flex: 0 0 23px;
        height: 19px;

        path {
          fill: ${colors.main};
          stroke: ${colors.main};
        }
      }
    `}
`;

const Option = styled.div<{ isActive?: boolean }>`
  ${OptionStyles}
`;

const OptionLink = styled(Link)`
  ${OptionStyles}
`;

const NoOptions = styled.p`
  padding: 10px 15px;
  font-size: 16px;
  line-height: 19px;
  color: ${(props) => props.theme.blue100};
`;
